import { useMemo, useState } from 'react';
import { useDebouncedValue } from 'hooks/useDebouncedvalue';
import { useCodeCheckQuery, useRegisterUserMutation } from 'services/signupService';
import { usePasswordChecklist } from 'features/signUp/hooks/usePasswordChecklist';
import { isValidEmailAddress } from 'utils/inputProcessingUtils/inputProcessingUtils';
import { CodeCheckApiResponse } from 'features/signUp/types';
import { useAppDispatch } from 'store/store';
import { setCredentials, setIsLoggedIn } from 'features/auth/authSlice';

const EMAIL_DOMAIN = '@wellframe.com';

enum AccessCodeErrorType {
  INVALID = 'invalid',
  REQUIRES_ELIGIBILITY = 'requires_eligibility',
  SSO_NOT_SUPPORTED = 'sso_not_supported',
}

/**
 * Determines the error type for an access code based on the API response.
 */
const getAccessCodeErrorType = (
  codeCheckData: CodeCheckApiResponse | undefined,
): AccessCodeErrorType | null => {
  if (!codeCheckData) return null;
  if (!codeCheckData.valid) return AccessCodeErrorType.INVALID;
  if (codeCheckData.requires_eligibility) return AccessCodeErrorType.REQUIRES_ELIGIBILITY;
  if (codeCheckData.authConfig) return AccessCodeErrorType.SSO_NOT_SUPPORTED;
  return null;
};

/**
 * Returns a user-friendly error message for the given access code error type.
 */
const getAccessCodeErrorMessage = (errorType: AccessCodeErrorType | null): string => {
  switch (errorType) {
    case AccessCodeErrorType.INVALID:
      return 'Invalid access code';
    case AccessCodeErrorType.REQUIRES_ELIGIBILITY:
      return 'Access code requires eligibility check';
    case AccessCodeErrorType.SSO_NOT_SUPPORTED:
      return 'SSO signup not supported';
    default:
      return '';
  }
};

/**
 * Custom hook for managing test user signup form state and validation.
 * Handles access code validation, email formatting, and password requirements.
 *
 * @returns Form state, handlers, and validation results
 */
export const useSignUpTestUser = () => {
  const appDispatch = useAppDispatch();
  // Access Code
  const [accessCode, setAccessCode] = useState('');
  const { debouncedValue: debouncedAccessCode } = useDebouncedValue(accessCode, 500);
  const { data: codeCheckData, isFetching: isCodeCheckFetching } = useCodeCheckQuery(
    debouncedAccessCode,
    {
      skip: !debouncedAccessCode,
    },
  );

  const isCodeCheckPending = debouncedAccessCode !== accessCode || isCodeCheckFetching;
  const accessCodeErrorType = useMemo(() => {
    if (isCodeCheckPending) return null;
    return getAccessCodeErrorType(codeCheckData);
  }, [isCodeCheckPending, codeCheckData]);
  const accessCodeError = accessCodeErrorType !== null;
  const accessCodeHelperText = useMemo(() => {
    if (isCodeCheckPending) return 'Checking code...';
    return getAccessCodeErrorMessage(accessCodeErrorType);
  }, [isCodeCheckPending, accessCodeErrorType]);

  // Email
  const [email, setEmail] = useState('');
  const emailWithDomain = useMemo(() => `${email}${EMAIL_DOMAIN}`, [email]);
  const emailError = useMemo(() => {
    if (!email) return false;
    return !isValidEmailAddress(emailWithDomain);
  }, [email, emailWithDomain]);

  // Password
  const [password, setPassword] = useState('');
  const { validatePassword, validationResults: passwordValidationResults } = usePasswordChecklist(
    password,
    !!password,
  );
  const passwordError = useMemo(
    () => (password ? !validatePassword(password) : false),
    [password, validatePassword],
  );

  // Form
  const isFormValid = useMemo(() => {
    const hasAllRequiredFields = Boolean(accessCode && email && password);
    const hasNoValidationErrors = !accessCodeError && !emailError && !passwordError;
    const isNotPending = !isCodeCheckPending;

    return hasAllRequiredFields && hasNoValidationErrors && isNotPending;
  }, [accessCode, email, password, accessCodeError, emailError, passwordError, isCodeCheckPending]);
  const [registerUser, { isLoading: isRegisterUserLoading }] = useRegisterUserMutation();

  const handleSubmit = async () => {
    if (!codeCheckData) return;
    try {
      const registeredUser = await registerUser({
        requestBody: {
          program_user: {
            code: debouncedAccessCode,
            status: 'registered',
            user_type: 'regular',
            first_name: 'Test',
            last_name: 'User',
            dob: '1970-01-01',
            gender: 'Other',
            state: 'MA',
            phone_number: '6175555555',
            email: emailWithDomain,
            password,
            eula_policy: codeCheckData.eula_policy,
            privacy_policy: codeCheckData.privacy_policy,
          },
        },
      }).unwrap();
      appDispatch(
        setCredentials({
          accessToken: registeredUser?.accessToken,
          refreshToken: registeredUser?.refreshToken,
        }),
      );
      appDispatch(setIsLoggedIn(true));
    } catch (error) {
      console.error('Error registering user:', error);
    }
  };

  return {
    // Access Code
    accessCode,
    setAccessCode,
    accessCodeError,
    accessCodeHelperText,
    // Email
    email,
    setEmail,
    emailWithDomain,
    emailError,
    emailDomain: EMAIL_DOMAIN,
    // Password
    password,
    setPassword,
    passwordError,
    passwordValidationResults,
    // Form
    isFormValid,
    handleSubmit,
    isRegisterUserLoading,
  };
};
